/**
 * Analyzer failures the technologist cannot fix by re-scanning: a crash, a
 * timeout, an unreadable response.
 *
 * The app server marks them with `extensions.code === 'ANALYSIS_SYSTEM_ERROR'`
 * (server/src/util/errors.util.ts). The message is written for the
 * technologist and says whether trying again can help; the phantom-testing
 * flow shows it on the study page, like a user-facing error. The other
 * extensions are for the issue tracker: the failure's code, the phantom, the
 * technical line and the test id the analyzer was called with.
 */
import { codeOf, graphQLErrorsOf } from './graphqlErrors'

export const ANALYSIS_SYSTEM_ERROR_CODE = 'ANALYSIS_SYSTEM_ERROR' // must match server/src/util/errors.util.ts

export interface AnalysisFailure {
  /** shown to the technologist */
  message: string
  /** stable code of the failure, e.g. ANALYSIS_FAILED or FUNCTION_ERROR */
  reason: string | undefined
  /** the routine's phantom, e.g. ct_acr */
  phantom: string | undefined
  /** the technical line, e.g. "ValueError: min() iterable argument is empty" */
  detail: string | undefined
  /** the test id the analyzer was called with */
  testId: number | undefined
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined
}

/**
 * The analyzer system failure carried by a thrown value (an ApolloError, a
 * bare GraphQL error object, ...), or undefined when it carries none.
 */
export function getAnalysisFailure(e: unknown): AnalysisFailure | undefined {
  const failure = graphQLErrorsOf(e).find(
    (error) => codeOf(error) === ANALYSIS_SYSTEM_ERROR_CODE,
  )
  // an empty message would make a blank alert; treat it as not one of these
  const message = text(failure?.message)
  if (!failure || message === undefined) {
    return undefined
  }
  const { reason, phantom, detail, testId } = failure.extensions
  return {
    message,
    reason: text(reason),
    phantom: text(phantom),
    detail: text(detail),
    testId: typeof testId === 'number' ? testId : undefined,
  }
}

/** The message for the technologist, or undefined when the error is not an analyzer system failure. */
export function getAnalysisFailureMessage(e: unknown): string | undefined {
  return getAnalysisFailure(e)?.message
}

export const isAnalysisFailure = (e: unknown): boolean =>
  getAnalysisFailure(e) !== undefined
